/*
 * Combobox — single-select avec recherche, bâti sur shadcn Popover + Command
 * (cmdk). Remplace `@mui/material/Autocomplete` dans le Lot 5 de la migration
 * shadcn (2026-04-30).
 *
 * Pourquoi un wrapper local plutôt que la "recipe" shadcn copiée dans chaque
 * appelant : 10 callsites dans le repo, tous avec la même forme `{label, value}`
 * issue de `adaptSelectActsOptions / adaptSelectDrugsOptions`. Centraliser ici
 * évite 10 copies de Popover+Command et garde le diff de migration minimal —
 * chaque appelant passe d'un `<Autocomplete>` à un `<Combobox>` avec quasiment
 * les mêmes props.
 *
 * API :
 *   - `options: { label, value, ... }[]` — extras conservés (ex: actsTypeName
 *     dont dépend FinancialTransactionModal pour son onChange).
 *   - `value` optionnel → mode contrôlé. Sinon mode uncontrolled, comme l'ancien
 *     MUI Autocomplete sans prop `value` (la majorité des callsites legacy).
 *   - `onChange(option)` reçoit l'objet sélectionné entier (pas juste la value).
 *     Volontaire : les callsites font `val.value` ET parfois `val.actsTypeName`
 *     ou `val.label`. L'ancien MUI passait `(e, val)` ; ici on drop le `e` non
 *     utilisé partout sauf un endroit (Payment, qui ne s'en sert pas non plus).
 *   - `renderItem` optionnel → pour le cas Payment qui rend une fiche riche
 *     (montant, statut, méthode). Reçoit `(option, isSelected)`. Combobox
 *     conserve la gestion du `CommandItem` (clavier, sélection, fermeture).
 *   - `filterValue` optionnel → string concatenant les champs searchables côté
 *     consommateur. Sinon cmdk filtre sur `option.label` seul.
 */
"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  comboboxTriggerClass,
  multiComboboxTriggerClass,
} from "@/lib/formStyles";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type ComboboxOption = {
  label: string;
  value: string | number;
  // Champs additionnels préservés — les onChange legacy lisent parfois
  // `val.actsTypeName`, `val.id`, etc. Pas de typing strict ici, c'est le
  // prix à payer pour une migration minimum-diff.
  [key: string]: any;
};

type ComboboxProps = {
  options: ComboboxOption[];
  /** Mode contrôlé : valeur actuellement sélectionnée. Comparée par String(). */
  value?: ComboboxOption["value"] | null;
  /** Callback de sélection. Reçoit l'option entière (pas juste `value`). */
  onChange?: (option: ComboboxOption) => void;
  /** Texte du trigger quand rien n'est sélectionné. */
  placeholder?: string;
  /** Placeholder du champ de recherche dans le popover. */
  searchPlaceholder?: string;
  /** Texte affiché quand aucune option ne matche la recherche. */
  emptyText?: React.ReactNode;
  /** Largeur / styling du trigger. */
  className?: string;
  /** Styling additionnel du PopoverContent. */
  popoverClassName?: string;
  disabled?: boolean;
  /**
   * Rendu custom de chaque item (cas Payment). Reçoit l'option et l'état
   * sélectionné ; le check par défaut (Check icon) n'est PAS rendu si fourni
   * — au consommateur de gérer le visuel de sélection.
   */
  renderItem?: (
    option: ComboboxOption,
    isSelected: boolean,
  ) => React.ReactNode;
  /**
   * Override de la chaîne searchable utilisée par cmdk. Par défaut = label.
   * Utiliser pour searcher sur plusieurs champs (ex: `${o.label} ${o.account}`).
   */
  getFilterValue?: (option: ComboboxOption) => string;
  /** Optionnel : afficher le label du trigger même quand `value` n'a pas
   * d'option correspondante (utile en mode contrôlé avec options async). */
  fallbackLabel?: string;
  /**
   * Forward la valeur tapée dans le CommandInput. Si fourni, le combobox
   * passe en mode async : par défaut cmdk désactive son filtre interne
   * (le serveur filtre) — le consommateur fournit déjà des `options` filtrées.
   */
  onSearchChange?: (q: string) => void;
  /** Affiche un état de chargement dans la liste pendant la recherche. */
  loading?: boolean;
  /**
   * Override explicite du filtre cmdk. Default : `true` en mode local
   * (sans `onSearchChange`), `false` en mode async (avec `onSearchChange`).
   */
  shouldFilter?: boolean;
};

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Sélectionner...",
  searchPlaceholder = "Rechercher...",
  emptyText = "Aucun résultat",
  className,
  popoverClassName,
  disabled,
  renderItem,
  getFilterValue,
  fallbackLabel,
  onSearchChange,
  loading,
  shouldFilter,
}: ComboboxProps) {
  const [search, setSearch] = React.useState("");
  const isAsync = onSearchChange != null;
  const filterEnabled = shouldFilter ?? !isAsync;
  const [open, setOpen] = React.useState(false);

  // Comparaison stringifiée : les `value` peuvent être number côté API
  // (ex: id de drugs) et string côté option mappée. String() évite la
  // surprise classique `42 !== "42"` ici.
  const selected = React.useMemo(
    () =>
      value == null
        ? undefined
        : options.find((o) => String(o.value) === String(value)),
    [options, value],
  );

  // Trigger = <button> plat (plus de variant outline du Button shadcn)
  // — voir `src/lib/formStyles.ts` pour le rationale : on aligne le visuel
  // sur les <input>/<select> du reste de l'app (h-11, slate-50/70, focus
  // emerald). `cn()` + tailwind-merge laissent le consommateur surcharger
  // (ex. `w-[300px]`).
  const showPlaceholderColor = !selected && !fallbackLabel;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            comboboxTriggerClass,
            showPlaceholderColor && "text-slate-400",
            className,
          )}
        >
          <span className="truncate">
            {selected?.label ?? fallbackLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Aligner la largeur sur le trigger : Radix expose
        // `--radix-popover-trigger-width` au runtime. Plus robuste que
        // de figer un `w-[300px]` qui ne suit pas le className passé.
        className={cn(
          "w-[--radix-popover-trigger-width] p-0",
          popoverClassName,
        )}
        align="start"
      >
        <Command shouldFilter={filterEnabled}>
          <CommandInput
            placeholder={searchPlaceholder}
            // Contrôlé uniquement en mode async — sinon cmdk gère son
            // propre state interne (préserve le comportement legacy).
            value={isAsync ? search : undefined}
            onValueChange={
              isAsync
                ? (v: string) => {
                    setSearch(v);
                    onSearchChange?.(v);
                  }
                : undefined
            }
          />
          <CommandList>
            {loading ? (
              <div className="px-3 py-3 text-center text-sm text-slate-500">
                Recherche…
              </div>
            ) : (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            <CommandGroup>
              {options.map((opt) => {
                const isSelected =
                  selected != null && selected.value === opt.value;
                // cmdk filtre par la prop `value` du CommandItem (substring,
                // case-insensitive). On garde `key` séparé pour React.
                const filterValue = getFilterValue
                  ? getFilterValue(opt)
                  : opt.label;
                return (
                  <CommandItem
                    key={String(opt.value)}
                    value={filterValue}
                    onSelect={() => {
                      onChange?.(opt);
                      setOpen(false);
                    }}
                  >
                    {renderItem ? (
                      renderItem(opt, isSelected)
                    ) : (
                      <>
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="truncate">{opt.label}</span>
                      </>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/*
 * MultiCombobox — variante multi-sélection. Remplace `react-select` avec
 * `isMulti` dans le Lot 7 de la migration shadcn (2026-04-30).
 *
 * Différences clés avec Combobox single :
 *   - `value` est un tableau de `ComboboxOption` (pas une primitive). On
 *     conserve la forme `{value, label, ...extras}` car les callsites legacy
 *     (TabOne MEDICAL_HISTORY/FAMILY_HISTORY) lisent `historyTypeName` et
 *     `diseasesId` sur chaque entrée sélectionnée.
 *   - Le popover ne se referme PAS après chaque sélection (cohérent avec UX
 *     multi-select standard). On toggle l'élément cliqué.
 *   - Le trigger affiche les badges sélectionnés avec un X par badge pour
 *     retirer rapidement (cas le plus fréquent du formulaire patient).
 */
type MultiComboboxProps = {
  options: ComboboxOption[];
  /** Valeurs sélectionnées (objets entiers, pas juste les `value`). */
  value: ComboboxOption[];
  /** Callback avec le nouveau tableau d'options sélectionnées. */
  onChange: (options: ComboboxOption[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: React.ReactNode;
  className?: string;
  popoverClassName?: string;
  disabled?: boolean;
  getFilterValue?: (option: ComboboxOption) => string;
  /** Voir `ComboboxProps.onSearchChange` — passe le combobox en mode async. */
  onSearchChange?: (q: string) => void;
  /** Voir `ComboboxProps.loading`. */
  loading?: boolean;
  /** Voir `ComboboxProps.shouldFilter`. */
  shouldFilter?: boolean;
};

export function MultiCombobox({
  options,
  value,
  onChange,
  placeholder = "Sélectionner...",
  searchPlaceholder = "Rechercher...",
  emptyText = "Aucun résultat",
  className,
  popoverClassName,
  disabled,
  getFilterValue,
  onSearchChange,
  loading,
  shouldFilter,
}: MultiComboboxProps) {
  const [search, setSearch] = React.useState("");
  const isAsync = onSearchChange != null;
  const filterEnabled = shouldFilter ?? !isAsync;
  const [open, setOpen] = React.useState(false);

  // Set des `value` sélectionnées, comparées en string. Idem Combobox single :
  // on couvre les cas où l'API renvoie des `number` et les options des `string`.
  const selectedKeys = React.useMemo(
    () => new Set(value.map((v) => String(v.value))),
    [value],
  );

  const toggle = (opt: ComboboxOption) => {
    if (selectedKeys.has(String(opt.value))) {
      onChange(value.filter((v) => String(v.value) !== String(opt.value)));
    } else {
      onChange([...value, opt]);
    }
  };

  const remove = (opt: ComboboxOption, e: React.MouseEvent) => {
    // Stop propagation : sinon le clic remonte au PopoverTrigger et ouvre
    // le popover, ce qui n'est pas le geste attendu pour un retrait.
    e.stopPropagation();
    onChange(value.filter((v) => String(v.value) !== String(opt.value)));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            multiComboboxTriggerClass,
            value.length === 0 && "text-slate-400",
            className,
          )}
        >
          {value.length === 0 ? (
            <span>{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1 py-0.5">
              {value.map((v) => (
                <span
                  key={String(v.value)}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                >
                  {v.label}
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => remove(v, e)}
                    className="cursor-pointer text-emerald-600 hover:text-rose-600"
                    aria-label={`Retirer ${v.label}`}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </span>
              ))}
            </div>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-[--radix-popover-trigger-width] p-0",
          popoverClassName,
        )}
        align="start"
      >
        <Command shouldFilter={filterEnabled}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={isAsync ? search : undefined}
            onValueChange={
              isAsync
                ? (v: string) => {
                    setSearch(v);
                    onSearchChange?.(v);
                  }
                : undefined
            }
          />
          <CommandList>
            {loading ? (
              <div className="px-3 py-3 text-center text-sm text-slate-500">
                Recherche…
              </div>
            ) : (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = selectedKeys.has(String(opt.value));
                const filterValue = getFilterValue
                  ? getFilterValue(opt)
                  : opt.label;
                return (
                  <CommandItem
                    key={String(opt.value)}
                    value={filterValue}
                    onSelect={() => toggle(opt)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
